import { useState, useContext, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import api from "../api/client";
import { AuthContext } from "../auth/AuthContext";
import { usePortalData } from "../portal/context/PortalDataContext";
import { Message } from "primereact/message";
import LegalModal from "./LegalModal";

const S = {
  page: {
    minHeight: "100dvh",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    background: "linear-gradient(155deg, #0d4f7a 0%, #1a6fa8 45%, #0d9488 100%)",
    padding: "20px 16px",
    fontFamily: "'DM Sans', sans-serif",
  },
  card: {
    background: "#ffffff",
    borderRadius: 20,
    padding: "36px 32px",
    width: "100%",
    maxWidth: 420,
    boxShadow: "0 20px 60px rgba(0,0,0,0.2)",
  },
  logoBox: {
    width: 48,
    height: 48,
    borderRadius: 14,
    background: "linear-gradient(135deg, #1a6fa8, #0d9488)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    margin: "0 auto 20px",
  },
  heading: {
    fontSize: 18,
    fontWeight: 600,
    color: "#0f2944",
    margin: "0 0 6px",
    textAlign: "center",
    fontFamily: "'Lora', Georgia, serif",
  },
  sub: {
    fontSize: 13,
    color: "#4a6080",
    textAlign: "center",
    margin: "0 0 4px",
    lineHeight: 1.5,
  },
  phone: {
    fontSize: 14,
    fontWeight: 600,
    color: "#1a6fa8",
    textAlign: "center",
    margin: "0 0 28px",
  },
  digitBox: {
    width: 48,
    height: 56,
    borderWidth: "2px",      
    borderStyle: "solid",     
    borderColor: "#e2eaf3",  
    borderRadius: 12,
    textAlign: "center",
    fontSize: 22,
    fontWeight: 700,
    fontFamily: "'DM Sans', sans-serif",
    color: "#0f2944",
    outline: "none",
    background: "#f8fafc",
    transition: "all 0.15s",
    caretColor: "transparent",
  },
  digitBoxFocused: {
    borderColor: "#1a6fa8",
    background: "#fff",
    boxShadow: "0 0 0 3px #e8f4fd",
  },
  digitBoxFilled: {
    borderColor: "#0d9488",
    background: "#f0fdf9",
  },
  btn: {
    width: "100%",
    padding: "13px",
    border: "none",
    borderRadius: 10,
    background: "linear-gradient(135deg, #1a6fa8, #0d9488)",
    color: "white",
    fontSize: 14,
    fontWeight: 600,
    fontFamily: "'DM Sans', sans-serif",
    cursor: "pointer",
    marginTop: 24,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    transition: "opacity 0.15s",
  },
  backBtn: {
    background: "none",
    border: "1.5px solid #e2eaf3",
    borderRadius: 10,
    padding: "10px",
    width: "100%",
    fontSize: 13,
    fontWeight: 500,
    color: "#4a6080",
    cursor: "pointer",
    fontFamily: "'DM Sans', sans-serif",
    marginTop: 10,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 7,
    transition: "all 0.15s",
  },
  claimBox: {
    background: "#fffbeb",
    border: "1px solid #f59e0b",
    borderRadius: 10,
    padding: "14px 16px",
    fontSize: 13,
    color: "#92400e",
    lineHeight: 1.6,
    marginTop: 20,
    textAlign: "center",
  },
};

function LogoSvg({ size = 28 }) {
  return (
    <svg viewBox="0 0 100 100" width={size} height={size}>
      <circle cx="50" cy="24" r="10" fill="white"/>
      <line x1="50" y1="34" x2="50" y2="68" stroke="white" strokeWidth="7" strokeLinecap="round"/>
      <polyline points="50,51 30,45 35,28" fill="none" stroke="white" strokeWidth="7" strokeLinecap="round" strokeLinejoin="round"/>
      <polyline points="50,51 70,45 65,28" fill="none" stroke="white" strokeWidth="7" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M25,70 Q50,90 75,70" fill="none" stroke="white" strokeWidth="7" strokeLinecap="round"/>
    </svg>
  );
}

function normalizePhoneDigits(raw) {
  let digits = raw.replace(/\D/g, "");
  digits = digits.replace(/^0+/, "");   // local number never starts with 0
  return digits.length > 9 ? digits.slice(-9) : digits;
}

function OtpInput({ value, onChange, disabled }) {
  const inputRefs = useRef([]);
  const digits    = value.split("").concat(Array(6).fill("")).slice(0, 6);
  const [focusedIdx, setFocusedIdx] = useState(null);

  const handleChange = (idx, e) => {
    const char = e.target.value.replace(/\D/g, "").slice(-1);
    if (!char) return;
    const newDigits = [...digits];
    newDigits[idx]  = char;
    onChange(newDigits.join("").slice(0, 6));
    if (idx < 5) inputRefs.current[idx + 1]?.focus();
  };

  const handleKeyDown = (idx, e) => {
    if (e.key === "Backspace") {
      e.preventDefault();
      const newDigits = [...digits];
      if (newDigits[idx]) {
        newDigits[idx] = "";
        onChange(newDigits.join(""));
      } else if (idx > 0) {
        newDigits[idx - 1] = "";
        onChange(newDigits.join(""));
        inputRefs.current[idx - 1]?.focus();
      }
    } else if (e.key === "ArrowLeft"  && idx > 0) inputRefs.current[idx - 1]?.focus();
    else if   (e.key === "ArrowRight" && idx < 5) inputRefs.current[idx + 1]?.focus();
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (pasted) {
      onChange(pasted);
      inputRefs.current[Math.min(pasted.length, 5)]?.focus();
    }
  };

  useEffect(() => {
    const firstEmpty = digits.findIndex(d => !d);
    inputRefs.current[firstEmpty === -1 ? 5 : firstEmpty]?.focus();
  }, []);

  return (
    <div style={{ display: "flex", gap: 8, justifyContent: "center" }}>
      {digits.map((d, idx) => (
        <input
          key={idx}
          ref={el => inputRefs.current[idx] = el}
          type="text"
          inputMode="numeric"
          maxLength={1}
          value={d}
          disabled={disabled}
          onChange={(e) => handleChange(idx, e)}
          onKeyDown={(e) => handleKeyDown(idx, e)}
          onPaste={handlePaste}
          onFocus={() => setFocusedIdx(idx)}
          onBlur={() => setFocusedIdx(null)}
          style={{
            ...S.digitBox,
            ...(focusedIdx === idx ? S.digitBoxFocused : {}),
            ...(d ? S.digitBoxFilled : {}),
          }}
        />
      ))}
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────
export default function OtpVerify() {
  const { state }            = useLocation();
  const navigate             = useNavigate();
  const { markPhoneVerified, selectOrg, selectDepartment, phoneVerified } = useContext(AuthContext);
  const { refresh } = usePortalData();
  const pharmacyId = state?.pharmacyId || localStorage.getItem("pendingPharmacyId") || "";
  const departmentId = state?.departmentId || localStorage.getItem("pendingDepartmentId") || "";

  // phone may come pre-filled (future: from profile page)
  // or be empty (registration flow — user enters it here)
  const [phone,        setPhone]        = useState(state?.phone || "");
  const [phoneInput,   setPhoneInput]   = useState(""); // raw 9-digit input
  const [step,         setStep]         = useState(state?.phone ? "otp" : "phone"); // "phone" | "otp"
  const [otp,          setOtp]          = useState("");
  const [loading,      setLoading]      = useState(false);
  const [error,        setError]        = useState("");
  const [counter,      setCounter]      = useState(60);
  const [success,      setSuccess]      = useState(false);
  const [claimPending, setClaimPending] = useState(false);
  const [consentChecked, setConsentChecked] = useState(false);
  const [legalModal, setLegalModal] = useState(null); // "terms" | "privacy" | null

  // Countdown only runs during OTP step
  useEffect(() => {
    if (step !== "otp" || counter <= 0) return;
    const t = setTimeout(() => setCounter(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [counter, step]);

  // Auto-submit when 6 digits entered
  useEffect(() => {
    if (otp.length === 6) verify(otp);
  }, [otp]);

  const sendOtp = async () => {
    setError("");
    const digits = phoneInput.replace(/\D/g, "");
    if (digits.length !== 9) {
      setError("Please enter a valid 9-digit mobile number"); return;
    }
    // Consent only applies to first-time verification — an already-verified
    // account changing their number later has already consented once.
    if (!phoneVerified && !consentChecked) {
      setError("Please accept the consent notice to continue"); return;
    }
    const localPhone = "0" + digits;
    setLoading(true);
    try {
      await api.post("auth/phone/send/", { phone: localPhone, consent: !phoneVerified ? consentChecked : undefined });
      setPhone(localPhone);
      setStep("otp");
      setCounter(60);
    } catch (err) {
      setError(err.response?.data?.error ?? "Failed to send OTP. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const verify = async (code = otp) => {
    if (code.length !== 6) { setError("Please enter all 6 digits"); return; }
    setError("");
    setLoading(true);
    try {
      const res = await api.post("auth/phone/confirm/", {
        phone,
        otp: code,
        ...(pharmacyId ? { pharmacy_id: pharmacyId } : {}),
        ...(departmentId ? { department_id: departmentId } : {}),
      });
      if (res.data.claim_initiated) {
        setClaimPending(true);
        return;
      }
      setSuccess(true);
      setTimeout(() => {
        markPhoneVerified();
        const hadPendingPharmacy = !!localStorage.getItem("pendingPharmacyId");
        localStorage.removeItem("pendingPharmacyId");
        localStorage.removeItem("pendingDepartmentId");
        if (hadPendingPharmacy) {
          selectOrg(pharmacyId);
          if (departmentId) selectDepartment(departmentId);
          refresh();
        }
        navigate("/home");
      }, 800);
    } catch {
      setError("Invalid or expired code. Please try again.");
      setOtp("");
    } finally {
      setLoading(false);
    }
  };

  const resendOtp = async () => {
    try {
      await api.post("auth/phone/send/", { phone });
      setCounter(60);
      setOtp("");
      setError("");
    } catch {
      setError("Unable to resend code. Please try again.");
    }
  };

  const displayPhone = phone.replace(/^(0\d{2})(\d{3})(\d{4})$/, "$1 $2 $3");

  return (
    <div style={S.page}>
      <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600&family=Lora:wght@600&display=swap" rel="stylesheet" />

      <div style={S.card}>
        <div style={S.logoBox}><LogoSvg size={24} /></div>

        {/* ── Step 1: Phone entry ── */}
        {step === "phone" && (
          <>
            <p style={S.heading}>Verify your number</p>
            <p style={S.sub}>Enter your mobile number to connect with your pharmacy records.</p>

            {error && (
              <div style={{ marginBottom: 16 }}>
                <Message severity="error" text={error} style={{ width: "100%", borderRadius: 10 }} />
              </div>
            )}

            <div style={{
              display: "flex", gap: 0,
              border: "1.5px solid #e2eaf3", borderRadius: 10,
              overflow: "hidden", marginTop: 16,
            }}>
              <div style={{
                padding: "11px 14px", background: "#f0f5fa",
                fontSize: 14, fontWeight: 600, color: "#4a6080",
                borderRight: "1px solid #e2eaf3", whiteSpace: "nowrap",
              }}>
                🇱🇰 +94
              </div>
              <input
                type="tel"
                inputMode="numeric"
                maxLength={15}
                value={phoneInput}
                onChange={(e) => setPhoneInput(normalizePhoneDigits(e.target.value))}
                onPaste={(e) => {
                  // maxLength truncates pasted text to 9 chars BEFORE onChange
                  // ever fires, so a pasted "0712345678" would already be cut
                  // to "071234567" (losing the last digit) by the time we saw
                  // it. Reading the clipboard directly here bypasses that.
                  e.preventDefault();
                  const pasted = e.clipboardData.getData("text");
                  setPhoneInput(normalizePhoneDigits(pasted));
                }}
                onKeyDown={(e) => e.key === "Enter" && sendOtp()}
                placeholder="7X XXX XXXX"
                style={{
                  flex: 1, border: "none", outline: "none",
                  padding: "11px 14px", fontSize: 15,
                  fontFamily: "'DM Sans', sans-serif", color: "#0f2944",
                  background: "white", letterSpacing: "1px",
                }}
              />
            </div>

            {!phoneVerified && (
              <label style={{
                display: "flex", alignItems: "flex-start", gap: 10,
                marginTop: 16, fontSize: 12.5, color: "#4a6080",
                lineHeight: 1.5, cursor: "pointer",
              }}>
                <input
                  type="checkbox"
                  checked={consentChecked}
                  onChange={(e) => setConsentChecked(e.target.checked)}
                  style={{ marginTop: 2, flexShrink: 0, width: 16, height: 16, cursor: "pointer" }}
                />
                <span>
                  I agree to Coffer Smart collecting, storing and sharing my
                  health information (prescriptions, allergies, and related
                  records) with my pharmacy through FitMe, as described in the{" "}
                  <span
                    onClick={(e) => { e.preventDefault(); setLegalModal("privacy"); }}
                    style={{ color: "#1a6fa8", fontWeight: 600, cursor: "pointer", textDecoration: "underline" }}
                  >
                    Privacy Policy
                  </span>
                  {" "}and{" "}
                  <span
                    onClick={(e) => { e.preventDefault(); setLegalModal("terms"); }}
                    style={{ color: "#1a6fa8", fontWeight: 600, cursor: "pointer", textDecoration: "underline" }}
                  >
                    Terms of Service
                  </span>
                  .
                </span>
              </label>
            )}

            <button
              onClick={sendOtp}
              disabled={loading || (!phoneVerified && !consentChecked)}
              style={{ ...S.btn, marginTop: 16, opacity: (loading || (!phoneVerified && !consentChecked)) ? 0.75 : 1 }}
            >
              {loading
                ? <><i className="pi pi-spin pi-spinner" style={{ fontSize: 14 }} /> Sending…</>
                : <><i className="pi pi-send" style={{ fontSize: 14 }} /> Send Verification Code</>
              }
            </button>
          </>
        )}

        {/* ── Step 2: OTP entry ── */}
        {step === "otp" && (
          <>
            <p style={S.heading}>Enter the code</p>
            <p style={S.sub}>We sent a 6-digit code to</p>
            <p style={S.phone}>{displayPhone}</p>

            {error && (
              <div style={{ marginBottom: 20 }}>
                <Message severity="error" text={error} style={{ width: "100%", borderRadius: 10 }} />
              </div>
            )}

            {claimPending ? (
              <div style={S.claimBox}>
                <i className="pi pi-clock" style={{ fontSize: 20, color: "#f59e0b", display: "block", marginBottom: 8 }} />
                <strong>Number transfer initiated</strong><br />
                This number is linked to another account. We've notified them.
                If uncontested, it transfers to your account within 48 hours.
              </div>
            ) : (
              <>
                <OtpInput value={otp} onChange={setOtp} disabled={loading || success} />

                {otp.length > 0 && otp.length < 6 && (
                  <div style={{ textAlign: "center", fontSize: 12, color: "#8fa3bc", marginTop: 10 }}>
                    {6 - otp.length} digit{6 - otp.length !== 1 ? "s" : ""} remaining
                  </div>
                )}

                {success && (
                  <div style={{
                    textAlign: "center", fontSize: 13, color: "#059669",
                    fontWeight: 500, marginTop: 14,
                    display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
                  }}>
                    <i className="pi pi-check-circle" style={{ fontSize: 16 }} />
                    Verified! Connecting your pharmacy records…
                  </div>
                )}

                {!success && (
                  <button
                    onClick={() => verify()}
                    disabled={loading || otp.length !== 6}
                    style={{
                      ...S.btn,
                      opacity: (loading || otp.length !== 6) ? 0.65 : 1,
                      cursor: (loading || otp.length !== 6) ? "not-allowed" : "pointer",
                    }}
                  >
                    {loading
                      ? <><i className="pi pi-spin pi-spinner" style={{ fontSize: 14 }} /> Verifying…</>
                      : <><i className="pi pi-shield" style={{ fontSize: 14 }} /> Verify &amp; Link</>
                    }
                  </button>
                )}

                <div style={{ marginTop: 18, textAlign: "center" }}>
                  {counter > 0 ? (
                    <div style={{ fontSize: 13, color: "#8fa3bc" }}>
                      Resend in <span style={{ fontWeight: 600, color: "#4a6080" }}>{counter}s</span>
                    </div>
                  ) : (
                    <button onClick={resendOtp} style={{
                      background: "none", border: "none", cursor: "pointer",
                      fontSize: 13, fontWeight: 600, color: "#1a6fa8",
                      fontFamily: "'DM Sans', sans-serif", padding: "4px 0",
                      display: "inline-flex", alignItems: "center", gap: 6,
                    }}>
                      <i className="pi pi-refresh" style={{ fontSize: 12 }} />
                      Resend code
                    </button>
                  )}
                </div>

                <button
                  onClick={() => setStep("phone")}
                  style={S.backBtn}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = "#1a6fa8"; e.currentTarget.style.color = "#1a6fa8"; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = "#e2eaf3"; e.currentTarget.style.color = "#4a6080"; }}
                >
                  <i className="pi pi-arrow-left" style={{ fontSize: 12 }} />
                  Change number
                </button>
              </>
            )}
          </>
        )}
      </div>

      <div style={{ marginTop: 24, fontSize: 11.5, color: "rgba(255,255,255,0.5)", textAlign: "center" }}>
        Secure customer portal · Powered by Coffer Smart
      </div>

      <LegalModal type={legalModal} onClose={() => setLegalModal(null)} />
    </div>
  );
}